/**
 * ResetPasswordPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState, useEffect, useMemo } from "react";

import { toast } from "sonner";
import { Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import { resetPassword, resendResetOtp, setUserToken, formatUserApiError } from "../../lib/userApi";
import OtpInput from "../../components/auth/OtpInput";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function ResetPasswordPanel({ pending, onResetDone, onBack }) {
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const masked = useMemo(() => {
    const [u, d] = (pending.email || "").split("@");
    if (!u || !d) return pending.email;
    const head = u.slice(0, Math.min(2, u.length));
    const tail = u.length > 3 ? u.slice(-1) : "";
    return `${head}${"•".repeat(Math.max(2, u.length - head.length - tail.length))}${tail}@${d}`;
  }, [pending.email]);

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (code.length !== 6) {
      toast.error("الرجاء إدخال الرمز كاملاً (6 أرقام)");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("كلمة المرور يجب أن تكون 8 أحرف على الأقل");
      return;
    }
    if (newPwd !== confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setSubmitting(true);
    try {
      const res = await resetPassword(pending.email, code, newPwd);
      if (res.token) setUserToken(res.token);
      toast.success("تم تغيير كلمة المرور — جارٍ الدخول");
      await onResetDone();
    } catch (err) {
      toast.error(formatUserApiError(err));
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      await resendResetOtp(pending.email);
      toast.success("أرسلنا رمزاً جديداً إلى بريدك");
      setCooldown(60);
      setCode("");
    } catch (err) {
      toast.error(formatUserApiError(err));
    }
  };

  return (
    <>
      <GhostBackButton onClick={onBack} testid="reset-back-btn" />
      <section className={`${GLASS} p-5 sm:p-6`} data-testid="reset-password-panel">
        <h1 className="text-[22px] font-extrabold text-[#0A192F] leading-tight">إعادة تعيين كلمة المرور</h1>
        <p className="text-[12.5px] text-[#5A6470] mt-1 mb-5">
          أدخِل الرمز المرسَل إلى <span dir="ltr" className="font-bold text-[#0A192F]">{masked}</span> ثم اختر كلمة مرور جديدة. صلاحيّة الرمز 10 دقائق.
        </p>

        <OtpInput value={code} onChange={setCode} disabled={submitting} testid="reset-otp-input" />

        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <div>
            <FieldLabel>كلمة المرور الجديدة * (8 أحرف على الأقل)</FieldLabel>
            <PasswordInput
              data-testid="reset-new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div>
            <FieldLabel>تأكيد كلمة المرور الجديدة *</FieldLabel>
            <PasswordInput
              data-testid="reset-new-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div className="pt-1">
            <GoldButton
              type="submit"
              disabled={submitting || code.length !== 6 || !newPwd || !confirm}
              testid="reset-submit"
            >
              {submitting ? "جارٍ التحقّق…" : "تغيير كلمة المرور"}
            </GoldButton>
          </div>
        </form>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/60">
          <span className="text-[12px] text-[#5A6470]">لم يصِلك الرمز؟</span>
          <button
            type="button"
            data-testid="reset-resend-btn"
            onClick={handleResend}
            disabled={cooldown > 0}
            className="text-[12.5px] font-extrabold text-[#A88425] hover:underline disabled:no-underline disabled:text-[#8A8A8A] disabled:cursor-not-allowed"
          >
            {cooldown > 0 ? `إعادة الإرسال خلال ${cooldown} ث` : "إرسال رمز جديد"}
          </button>
        </div>
      </section>
    </>
  );
}


